import { useEffect, useMemo, useState } from 'react';
import { Link, Navigate, useLocation, useNavigate, useParams } from 'react-router-dom';
import { useAuth } from '../../../context/AuthContext';
import {
  fetchTournamentAdmins,
  fetchTournamentById,
  fetchTournaments,
  updateTournamentAdmins,
} from '../../../services/tournamentService';
import TableControls from '../../../components/common/TableControls';
import useTableControls from '../../../hooks/useTableControls';
import playerManagerImg from '../../../assets/img/player-manager.svg';
import auctionManagerImg from '../../../assets/img/auction-manager.svg';
import matchManagerImg from '../../../assets/img/match-manager.svg';
import TournamentList from '../../../components/common/lists/TournamentList';
import { getUserRoles } from '../../../utils/userRoles';

const SHOW_ARCHIVED_STORAGE_KEY = 'crickarena.tournament-admin.show-archived';

const TournamentAdminTournamentsPage = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const {
    communityId: routeCommunityId,
    organizationId: routeOrganizationId,
    clubId: routeClubId,
    adminId: routeAdminId,
    tournamentId: routeTournamentId,
  } = useParams();
  const { token, user } = useAuth();
  const userRoles = getUserRoles(user);
  const isOrganizationAdmin = userRoles.has('organization_admin');
  const isCommunityAdmin = userRoles.has('community_admin');
  const isClubAdmin = userRoles.has('club_admin');
  const isTournamentAdmin = userRoles.has('tournament_admin');
  const isSuperAdmin = userRoles.has('super_admin');
  const isTeamAdmin = userRoles.has('department_admin') || userRoles.has('team_admin');
  const hasTournamentAdminAccess =
    isSuperAdmin || isTournamentAdmin || isOrganizationAdmin || isCommunityAdmin || isClubAdmin || isTeamAdmin;
  const [showArchived, setShowArchived] = useState(() => {
    if (typeof window === 'undefined') return false;
    return window.localStorage.getItem(SHOW_ARCHIVED_STORAGE_KEY) === 'true';
  });

  const [tournaments, setTournaments] = useState([]);
  const [loadError, setLoadError] = useState('');
  const [isLoading, setIsLoading] = useState(true);
  const [tournamentManagers, setTournamentManagers] = useState({});
  const [managerStatus, setManagerStatus] = useState({});
  const [savingManagers, setSavingManagers] = useState({});
  const [managerCandidates, setManagerCandidates] = useState([]);

  const getTournamentId = (tournament) => {
    if (!tournament) return '';

    const rawId =
      tournament.id ||
      tournament._id?.toString?.() ||
      tournament.tournamentId ||
      tournament._id;

    if (!rawId) return '';

    return rawId.toString ? rawId.toString() : `${rawId}`;
  };

  const normalizeId = (value) => {
    if (!value) return '';

    return value.toString ? value.toString() : `${value}`;
  };

  const normalizedUserId = useMemo(() => normalizeId(user?.id || user?._id), [user?.id, user?._id]);
  const adminId = routeAdminId || normalizedUserId;
  const entityType = routeCommunityId ? 'community' : routeClubId ? 'club' : routeOrganizationId ? 'organization' : '';
  const entityId = routeCommunityId || routeClubId || routeOrganizationId || '';
  const communityId = entityType === 'community' ? entityId : '';
  const clubId = entityType === 'club' ? entityId : '';
  const organizationId = entityType === 'organization' ? entityId : '';
  const tournamentsBasePath =
    entityType === 'community'
      ? `/community/${entityId}/tournament-admin/${adminId}/tournaments`
      : entityType === 'club'
        ? `/club/${entityId}/tournament-admin/${adminId}/tournaments`
        : entityType === 'organization'
          ? `/org/${entityId}/tournament-admin/${adminId}/tournaments`
          : null;
  const scopedEntityType = useMemo(() => {
    if (location.pathname.startsWith('/community/')) return 'community';
    if (location.pathname.startsWith('/club/')) return 'club';
    if (location.pathname.startsWith('/org/')) return 'organization';
    return entityType;
  }, [entityType, location.pathname]);
  const scopedEntityLabel =
    scopedEntityType === 'community'
      ? 'Community'
      : scopedEntityType === 'club'
        ? 'Club'
        : scopedEntityType === 'organization'
          ? 'Organization'
          : '';
  const scopedEntityTournamentsPath =
    scopedEntityType === 'community' && routeCommunityId
      ? `/community/${routeCommunityId}/tournament-admin/${adminId}/tournaments`
      : scopedEntityType === 'club' && routeClubId
        ? `/club/${routeClubId}/tournament-admin/${adminId}/tournaments`
        : scopedEntityType === 'organization' && routeOrganizationId
          ? `/org/${routeOrganizationId}/tournament-admin/${adminId}/tournaments`
          : null;
  const managerWorkspacePath =
    routeTournamentId && entityType === 'community'
      ? `/community/${entityId}/managers/tournaments/${routeTournamentId}`
      : routeTournamentId && entityType === 'club'
        ? `/club/${entityId}/managers/tournaments/${routeTournamentId}`
        : routeTournamentId && entityType === 'organization'
          ? `/org/${entityId}/managers/tournaments/${routeTournamentId}`
          : '/dashboard/individual/home';

  useEffect(() => {
    if (typeof window !== 'undefined') {
      window.localStorage.setItem(SHOW_ARCHIVED_STORAGE_KEY, String(showArchived));
    }
  }, [showArchived]);

  useEffect(() => {
    const loadTournaments = async () => {
      if (!token || !hasTournamentAdminAccess) return;

      setIsLoading(true);
      setLoadError('');

      try {
        const [tournamentResponse, adminResponse] = await Promise.all([
          fetchTournaments(token, organizationId, { communityId, clubId }),
          fetchTournamentAdmins(token, organizationId, { includeUsers: true, communityId, clubId }),
        ]);
        const tournamentsData = tournamentResponse.tournaments || [];

        setManagerCandidates(adminResponse?.users || []);
        setTournaments(tournamentsData);
        setTournamentManagers(
          tournamentsData.reduce((acc, tournament) => {
            const id = getTournamentId(tournament);
            acc[id] = {
              registrationManagerId:
                tournament.registrationManager?.id || tournament.registrationManager?._id || '',
              auctionManagerId: tournament.auctionManager?.id || tournament.auctionManager?._id || '',
              matchManagerId: tournament.matchManager?.id || tournament.matchManager?._id || '',
            };
            return acc;
          }, {}),
        );

        if (
          routeTournamentId &&
          !tournamentsData.some((tournament) => getTournamentId(tournament) === routeTournamentId)
        ) {
          const detailResponse = await fetchTournamentById(routeTournamentId, token, organizationId, {
            communityId,
            clubId,
          });
          if (detailResponse?.tournament) {
            setTournaments((prev) => {
              const exists = prev.some((item) => getTournamentId(item) === routeTournamentId);
              return exists ? prev : [detailResponse.tournament, ...prev];
            });
            setTournamentManagers((prev) => ({
              ...prev,
              [routeTournamentId]: {
                registrationManagerId:
                  detailResponse.tournament.registrationManager?.id ||
                  detailResponse.tournament.registrationManager?._id ||
                  '',
                auctionManagerId:
                  detailResponse.tournament.auctionManager?.id ||
                  detailResponse.tournament.auctionManager?._id ||
                  '',
                matchManagerId:
                  detailResponse.tournament.matchManager?.id ||
                  detailResponse.tournament.matchManager?._id ||
                  '',
              },
            }));
          }
        }
      } catch (error) {
        setLoadError(error.message || 'Unable to load tournaments.');
      } finally {
        setIsLoading(false);
      }
    };

    loadTournaments();
  }, [clubId, communityId, hasTournamentAdminAccess, organizationId, routeTournamentId, token]);

  const assignedTournaments = useMemo(() => {
    if (!Array.isArray(tournaments)) return [];

    if (isSuperAdmin || isOrganizationAdmin) return tournaments;

    if (!isTournamentAdmin || !normalizedUserId) return tournaments;

    return tournaments.filter((tournament) => {
      const adminMatch = (tournament.admins || []).some(
        (admin) => normalizeId(admin.id || admin._id) === normalizedUserId,
      );

      const managerMatch = ['registrationManager', 'auctionManager', 'matchManager'].some((role) =>
        normalizeId(tournament[role]?.id || tournament[role]?._id) === normalizedUserId,
      );

      return adminMatch || managerMatch;
    });
  }, [isOrganizationAdmin, isSuperAdmin, isTournamentAdmin, normalizedUserId, tournaments]);

  const managerName = (manager) => manager?.name || manager?.email || 'Not assigned';

  const normalizePlayersForDisplay = (players) =>
    (players || [])
      .map((player) => {
        if (!player) return null;

        if (typeof player === 'string') {
          return { id: player, name: 'Unknown player', role: '' };
        }

        const name =
          player.fullName ||
          player.name ||
          player.personal?.fullName ||
          player.user?.name ||
          player.user?.email ||
          'Unknown player';

        return {
          id: player.id || player._id || player._id?.toString?.() || player.toString?.(),
          name,
          role: player.primaryRole,
        };
      })
      .filter((player) => player && (player.id || player.name));

  const tournamentAdminOptions = useMemo(() => {
    const adminMap = new Map();

    const addAdminToMap = (admin) => {
      if (!admin) return;

      const adminId = admin.id || admin._id;
      if (!adminId) return;

      adminMap.set(adminId, { ...admin, id: adminId });
    };

    [...managerCandidates, ...tournaments.flatMap((tournament) => tournament.admins || [])].forEach(
      addAdminToMap,
    );

    tournaments.forEach((tournament) => {
      addAdminToMap(tournament.registrationManager);
      addAdminToMap(tournament.auctionManager);
      addAdminToMap(tournament.matchManager);
    });

    return Array.from(adminMap.values());
  }, [managerCandidates, tournaments]);

  const handleManagerChange = (tournamentId, field, value) => {
    setTournamentManagers((prev) => ({
      ...prev,
      [tournamentId]: {
        ...(prev[tournamentId] || {}),
        [field]: value,
      },
    }));
  };

  const handleSaveManagers = async (tournament) => {
    const tournamentId = getTournamentId(tournament);

    if (!tournamentId) return;

    const managers = tournamentManagers[tournamentId] || {};
    const adminIds = (tournament.admins || []).map((admin) => admin.id || admin._id).filter(Boolean);

    setSavingManagers((prev) => ({ ...prev, [tournamentId]: true }));
    setManagerStatus((prev) => ({
      ...prev,
      [tournamentId]: { tone: 'info', message: 'Saving managers...' },
    }));

    try {
      const response = await updateTournamentAdmins(tournamentId, adminIds, token, managers);
      const updatedTournament = response.tournament;

      if (updatedTournament) {
        replaceTournamentInState(updatedTournament);
        setTournamentManagers((prev) => ({
          ...prev,
          [tournamentId]: {
            registrationManagerId:
              updatedTournament.registrationManager?.id || updatedTournament.registrationManager?._id || '',
            auctionManagerId:
              updatedTournament.auctionManager?.id || updatedTournament.auctionManager?._id || '',
            matchManagerId:
              updatedTournament.matchManager?.id || updatedTournament.matchManager?._id || '',
          },
        }));
      }

      setManagerStatus((prev) => ({
        ...prev,
        [tournamentId]: { tone: 'success', message: 'Managers updated successfully.' },
      }));
    } catch (error) {
      setManagerStatus((prev) => ({
        ...prev,
        [tournamentId]: {
          tone: 'danger',
          message: error.message || 'Unable to save managers right now.',
        },
      }));
    } finally {
      setSavingManagers((prev) => ({ ...prev, [tournamentId]: false }));
    }
  };


  const replaceTournamentInState = (updatedTournament) => {
    setTournaments((prev) =>
      prev.map((tournament) => {
        const tournamentId = getTournamentId(tournament);
        const updatedId = getTournamentId(updatedTournament);
        return tournamentId === updatedId ? updatedTournament : tournament;
      })
    );

    const updatedId = getTournamentId(updatedTournament);
    if (updatedId) {
      setTournamentManagers((prev) => ({
        ...prev,
        [updatedId]: {
          registrationManagerId:
            updatedTournament.registrationManager?.id || updatedTournament.registrationManager?._id || '',
          auctionManagerId:
            updatedTournament.auctionManager?.id || updatedTournament.auctionManager?._id || '',
          matchManagerId: updatedTournament.matchManager?.id || updatedTournament.matchManager?._id || '',
        },
      }));
    }
  };

  const handleOpenTournament = (tournament) => {
    const id = getTournamentId(tournament);

    if (!id) return;

    if (!tournamentsBasePath) return;
    navigate(`${tournamentsBasePath}/${id}`);
  };

  const activeTournaments = useMemo(
    () => assignedTournaments.filter((tournament) => tournament.isActive !== false),
    [assignedTournaments]
  );

  const archivedTournaments = useMemo(
    () => assignedTournaments.filter((tournament) => tournament.isActive === false),
    [assignedTournaments]
  );

  const tournamentsForView = showArchived ? archivedTournaments : activeTournaments;

  const isDetailView = Boolean(routeTournamentId);

  const detailTournament = useMemo(() => {
    if (!routeTournamentId) return null;

    return tournaments.find(
      (tournament) => getTournamentId(tournament) === routeTournamentId
    );
  }, [routeTournamentId, tournaments]);

  const tournamentsForManagers = isDetailView && detailTournament ? [detailTournament] : [];

  const tournamentSorters = useMemo(
    () => ({
      name: (first, second) =>
        (first.name || 'Untitled tournament').localeCompare(
          second.name || 'Untitled tournament',
          undefined,
          { sensitivity: 'base' }
        ),
      status: (first, second) => Number(first.isActive === false) - Number(second.isActive === false),
      admins: (first, second) => (first.admins?.length || 0) - (second.admins?.length || 0),
      roster: (first, second) =>
        normalizePlayersForDisplay(first.players).length - normalizePlayersForDisplay(second.players).length,
      createdAt: (first, second) => new Date(first.createdAt) - new Date(second.createdAt),
    }),
    []
  );

  const tournamentsForList = isDetailView
    ? detailTournament
      ? [detailTournament]
      : []
    : tournamentsForView;

  const {
    paginatedItems: visibleTournaments,
    totalItems: totalTournaments,
    totalPages,
    page,
    pageSize,
    sortKey,
    sortDirection,
    setSortKey,
    setSortDirection,
    setPage,
    setPageSize,
    searchTerm,
    setSearchTerm,
    toggleDirection,
  } = useTableControls(tournamentsForList, {
    initialSortKey: 'createdAt',
    initialSortDirection: 'desc',
    sorters: tournamentSorters,
    searchKeys: [
      'name',
      (tournament) => (tournament.isActive !== false ? 'active' : 'archived'),
      (tournament) => (tournament.admins || []).map((admin) => admin.name || admin.email || '').join(' '),
    ],
  });

  if (!token) {
    return <Navigate to="/login" replace />;
  }

  if (!entityType || !entityId || !adminId || !tournamentsBasePath) {
    return <Navigate to="/dashboard/individual/home" replace />;
  }

  if (!hasTournamentAdminAccess) {
    if (isTeamAdmin) {
      const playerManagerPath =
        entityType === 'community'
          ? `/community/${entityId}/managers/${normalizedUserId}/tournaments`
          : entityType === 'club'
            ? `/club/${entityId}/managers/${normalizedUserId}/tournaments`
            : entityType === 'organization'
              ? `/org/${entityId}/managers/${normalizedUserId}/tournaments`
              : '/dashboard/individual/home';
      return <Navigate to={playerManagerPath} replace />;
    }

    return <Navigate to="/dashboard/individual/home" replace />;
  }

  const overviewTitle = isDetailView && detailTournament
    ? detailTournament.name || 'Untitled tournament'
    : showArchived
      ? 'Archived tournaments'
      : 'Tournaments assigned to you';

  const overviewDescription = isDetailView && detailTournament
    ? 'Review managers, players, and status for this tournament.'
    : showArchived
      ? 'Review archived tournaments, restore them, or delete them permanently.'
      : 'View the tournaments you manage and track their status from a single dashboard.';

  const detailNotFound = isDetailView && !isLoading && tournaments.length > 0 && !detailTournament;

  return (
    <div className="card shadow-sm border-0">
      <div className="card-body d-grid gap-4">
        <div className="d-flex flex-column flex-lg-row justify-content-lg-between gap-2">
          <div>
            <p className="eyebrow text-primary mb-1">Tournament overview</p>
            <h1 className="h4 mb-2">{overviewTitle}</h1>
            <p className="text-muted mb-0">{overviewDescription}</p>
          </div>
          <div className="d-flex align-items-start gap-2">
            <div className="d-flex flex-column text-end">
              <span className="fw-semibold">{activeTournaments.length} active</span>
              <span className="text-muted small">{archivedTournaments.length} archived</span>
            </div>
            <button
              type="button"
              className="btn btn-outline-primary"
              onClick={() =>
                (isDetailView ? navigate('/dashboard/tournament-admin/tournaments') : setShowArchived((prev) => !prev))
              }
            >
              {isDetailView ? 'Back to all tournaments' : showArchived ? 'Back to active tournaments' : 'View archived tournaments'}
            </button>
            {isDetailView && scopedEntityTournamentsPath ? (
              <button
                type="button"
                className="btn btn-outline-secondary"
                onClick={() => navigate(scopedEntityTournamentsPath)}
              >
                Back to {scopedEntityLabel} tournaments
              </button>
            ) : null}
          </div>
        </div>

        {isSuperAdmin && (
          <div className="alert alert-info py-2 mb-0">
            Viewing the tournament admin dashboard as a super admin. All tournaments are listed, but certain actions may
            require being assigned as an admin.
          </div>
        )}

        {detailNotFound && (
          <div className="alert alert-warning py-2 mb-0">
            Unable to find that tournament. Please return to the tournament list to try again.
          </div>
        )}

        {isDetailView && detailTournament && (
          <div className="card border-0 shadow-sm">
            <div className="card-body d-grid gap-4">
              <div className="d-flex flex-column flex-lg-row justify-content-lg-between gap-2">
                <div>
                  <p className="eyebrow text-primary mb-1">Tournament managers</p>
                  <h2 className="h5 mb-2">Assign registration, auction, and match managers</h2>
                  <p className="text-muted mb-0">
                    Open a tournament card to create or assign specialized managers for that tournament.
                  </p>
                </div>
                <span className="badge bg-secondary-subtle text-secondary-emphasis">Tournament admin view</span>
              </div>

              <div className="row row-cols-1 row-cols-md-3 g-3">
                {[
                  {
                    label: 'Player manager',
                    value: detailTournament.registrationManager,
                    image: playerManagerImg,
                  },
                  { label: 'Auction manager', value: detailTournament.auctionManager, image: auctionManagerImg },
                  { label: 'Match manager', value: detailTournament.matchManager, image: matchManagerImg },
                ].map((item) => (
                  <div key={item.label} className="col">
                    <div className="border rounded-3 p-3 h-100 bg-light-subtle">
                      <div className="d-flex align-items-center gap-3">
                        <div className="ratio ratio-1x1" style={{ width: '72px' }}>
                          <img
                            src={item.image}
                            alt={`${item.label} illustration`}
                            className="img-fluid rounded-2 border"
                          />
                        </div>
                        <div className="flex-grow-1">
                          <p className="small text-muted mb-1">{item.label}</p>
                          <p className="mb-0 fw-semibold">{managerName(item.value)}</p>
                          <p className="mb-0 small text-muted">
                            {item.value?.email || 'Assign a manager to start using their tools.'}
                          </p>
                        </div>
                      </div>
                    </div>
                  </div>
                ))}
              </div>

              <div className="alert alert-info mb-0" role="alert">
                Managers must already have a registered user account. Ask them to register first, then assign them
                from the dropdowns below.
              </div>

              <div className="table-responsive">
                <table className="table align-middle mb-0">
                  <thead>
                    <tr>
                      <th scope="col">Tournament</th>
                      <th scope="col">Player manager</th>
                      <th scope="col">Auction manager</th>
                      <th scope="col">Match manager</th>
                      <th scope="col" className="text-end">
                        <span className="visually-hidden">Actions</span>
                      </th>
                    </tr>
                  </thead>
                  <tbody>
                    {isLoading && (
                      <tr>
                        <td colSpan="5" className="text-center text-muted">
                          Loading tournaments...
                        </td>
                      </tr>
                    )}
                    {!isLoading && tournamentsForManagers.length === 0 && (
                      <tr>
                        <td colSpan="5" className="text-center text-muted">
                          No tournament available to assign managers.
                        </td>
                      </tr>
                    )}
                    {!isLoading &&
                      tournamentsForManagers.map((tournament) => {
                        const tournamentId = getTournamentId(tournament);
                        const managerSelection = tournamentManagers[tournamentId] || {};

                        return (
                          <tr key={tournamentId}>
                            <td className="fw-semibold">{tournament.name || 'Untitled tournament'}</td>
                            <td>
                              <select
                                className="form-select"
                                value={managerSelection.registrationManagerId || ''}
                                onChange={(event) =>
                                  handleManagerChange(tournamentId, 'registrationManagerId', event.target.value)
                                }
                              >
                                <option value="">No manager selected</option>
                                {tournamentAdminOptions.map((admin) => (
                                  <option key={admin.id} value={admin.id}>
                                    {admin.name} ({admin.email})
                                  </option>
                                ))}
                              </select>
                            </td>
                            <td>
                              <select
                                className="form-select"
                                value={managerSelection.auctionManagerId || ''}
                                onChange={(event) =>
                                  handleManagerChange(tournamentId, 'auctionManagerId', event.target.value)
                                }
                              >
                                <option value="">No manager selected</option>
                                {tournamentAdminOptions.map((admin) => (
                                  <option key={admin.id} value={admin.id}>
                                    {admin.name} ({admin.email})
                                  </option>
                                ))}
                              </select>
                            </td>
                            <td>
                              <select
                                className="form-select"
                                value={managerSelection.matchManagerId || ''}
                                onChange={(event) =>
                                  handleManagerChange(tournamentId, 'matchManagerId', event.target.value)
                                }
                              >
                                <option value="">No manager selected</option>
                                {tournamentAdminOptions.map((admin) => (
                                  <option key={admin.id} value={admin.id}>
                                    {admin.name} ({admin.email})
                                  </option>
                                ))}
                              </select>
                            </td>
                            <td className="text-end">
                              <div className="d-flex flex-column gap-2 align-items-end">
                                <button
                                  type="button"
                                  className="btn btn-outline-primary btn-sm"
                                  onClick={() => handleSaveManagers(tournament)}
                                  disabled={savingManagers[tournamentId]}
                                >
                                  {savingManagers[tournamentId] ? 'Saving...' : 'Save managers'}
                                </button>
                                {managerStatus[tournamentId]?.message && (
                                  <span
                                    className={`small text-${
                                      managerStatus[tournamentId].tone === 'success'
                                        ? 'success'
                                        : managerStatus[tournamentId].tone === 'info'
                                          ? 'muted'
                                          : 'danger'
                                    }`}
                                  >
                                    {managerStatus[tournamentId].message}
                                  </span>
                                )}
                              </div>
                            </td>
                          </tr>
                        );
                      })}
                  </tbody>
                </table>
              </div>

              <div className="d-flex justify-content-center">
                {routeTournamentId && (
                  <Link
                    to={managerWorkspacePath}
                    className="btn btn-primary"
                  >
                    Manager workspace
                  </Link>
                )}
              </div>
            </div>
          </div>
        )}

        {!isDetailView && (
          <div className="card border-0 shadow-sm">
            <div className="card-header bg-transparent border-0 pb-0">
              <div className="d-flex flex-column flex-sm-row align-items-sm-center justify-content-sm-between gap-2">
                <div>
                  <p className="text-muted small mb-1">Your tournaments</p>
                  <h2 className="h5 mb-0">Review tournament assignments</h2>
                </div>
                <span className="badge bg-secondary-subtle text-secondary-emphasis">Tournament admin view</span>
              </div>
            </div>

            <div className="card-body d-grid gap-3 pt-3">

              {loadError && <div className="alert alert-danger py-2 mb-0">{loadError}</div>}

              <TableControls
                sortKey={sortKey}
                sortDirection={sortDirection}
                onSortKeyChange={setSortKey}
                onSortDirectionChange={(direction) =>
                  direction ? setSortDirection(direction) : toggleDirection()
                }
                sortOptions={[
                  { value: 'createdAt', label: 'Created date' },
                  { value: 'name', label: 'Name' },
                  { value: 'status', label: 'Status' },
                  { value: 'admins', label: 'Admin count' },
                  { value: 'roster', label: 'Roster size' },
                ]}
                page={page}
                totalPages={totalPages}
                totalItems={totalTournaments}
                pageSize={pageSize}
                onPageChange={setPage}
                onPageSizeChange={setPageSize}
                searchTerm={searchTerm}
                onSearchChange={setSearchTerm}
                searchPlaceholder={`Search ${showArchived ? 'archived' : 'active'} tournaments or admins`}
              />


              <TournamentList
                tournaments={visibleTournaments}
                isAdminView
                entityType={entityType}
                onTournamentClick={handleOpenTournament}
                isLoading={isLoading}
                emptyStateMessage={
                  showArchived
                    ? 'No archived tournaments to show.'
                    : 'You have not been assigned to any tournaments yet.'
                }
              />
            </div>
          </div>
        )}

      </div>
    </div>
  );
};

export default TournamentAdminTournamentsPage;
